import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { packagesAPI } from '../services/api';
import { useDate } from './DateContext';

const PackageDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { checkIn, checkOut, formatDate } = useDate();
  const [packageItem, setPackageItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);

  useEffect(() => {
    fetchPackage();
  }, [id]);

  useEffect(() => {
    if (!isImageModalOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setIsImageModalOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isImageModalOpen]);

  const fetchPackage = async () => {
    try {
      const response = await packagesAPI.getById(id);
      setPackageItem(response.data);
    } catch (error) {
      console.error('Error fetching package:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBookPackage = () => {
    if (!packageItem) {
      return;
    }

    const phoneNumber = '9459457733'; // WhatsApp number (945 945 7733)
    
    // Build message with package details
    let message = 'Hello, I would like to book the following package:\n\n';
    message += `📦 Package: ${packageItem.title}\n`;
    if (checkIn && checkOut) {
      message += `📅 Check-in: ${formatDate(checkIn)}\n`;
      message += `📅 Check-out: ${formatDate(checkOut)}\n`;
    }
    if (packageItem.duration) {
      message += `⏱️ Duration: ${packageItem.duration}\n`;
    }
    
    if (packageItem.category) {
      message += `🏷️ Category: ${packageItem.category}\n`;
    }
    
    if (packageItem.price) {
      message += `💰 Price: ${packageItem.price}\n`;
    }
    
    if (packageItem.description) {
      message += `\n📝 Description:\n${packageItem.description}\n`;
    }
    
    if (packageItem.features && packageItem.features.length > 0) {
      message += `\n✅ What's Included:\n`;
      packageItem.features.forEach((feature, index) => {
        message += `${index + 1}. ${feature}\n`;
      });
    }
    
    message += '\n\nPlease confirm the booking. Thank you!';

    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
  };

  if (loading) {
    return (
      <div className="package-detail-loading">
      <div className="container">
        <div style={{ textAlign: 'center', padding: '50px' }}>
          Loading package details...
        </div>
      </div>
    </div>
  );
}

if (!packageItem) {
  return (
    <div className="package-detail-error">
      <div className="container">
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <h2>Package not found</h2>
          <button onClick={() => navigate('/home')} className="btn btn-primary">
            Back to Home
          </button>
        </div>
      </div>
    </div>
  );
}

return (
  <div className="package-detail-page has-hero">
    <Helmet>
      <title>{packageItem.title || 'Package'} | Holiday Packages | GoaDeals</title>
      <meta name="description" content={packageItem.description ? (packageItem.description.length > 155 ? `${packageItem.description.substring(0, 155)}...` : packageItem.description) : `Book ${packageItem.title} - Holiday package in Goa. GoaDeals.`} />
      <meta property="og:title" content={`${packageItem.title} | GoaDeals`} />
    </Helmet>

    <div className="container">
      {/* Back Button */}
      <div className="back-button">
        <button onClick={() => navigate('/home')} className="btn btn-secondary">
          ← Back to Packages
        </button>
      </div>

      {/* Package Header */}
      <div className="package-detail-header">
        <h1>{packageItem.title}</h1>
        <div className="package-meta">
          <span className="package-duration">{packageItem.duration}</span>
          <span className="package-category">{packageItem.category}</span>
        </div>
      </div>

      {/* Package Image - click to open full preview */}
      <div 
        className="package-image-section" 
        onClick={() => setIsImageModalOpen(true)}
        style={{ cursor: 'pointer' }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && setIsImageModalOpen(true)}
        aria-label="View full size image"
      >
        <img 
          src={packageItem.image} 
          alt={packageItem.title}
          className="package-main-image"
        />
      </div>

      {/* Package Details */}
      <div className="package-details-grid">
        <div className="package-info">
          <div className="package-description">
            <h3>Package Description</h3>
            <p>{packageItem.description}</p>
          </div>

          <div className="package-features">
            <h3>What's Included</h3>
            <ul>
              {packageItem.features.map((feature, index) => (
                <li key={index}>
                  <span className="feature-icon">✓</span>
                  {feature}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="package-booking">
          <div className="booking-card">
            <div className="package-price">
              <span className="price">{packageItem.price}</span>
              <span className="duration">{packageItem.duration}</span>
            </div>
            
            <div className="booking-actions">
              <button 
                className="btn btn-primary btn-large"
                onClick={handleBookPackage}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  backgroundColor: '#809947',
                  border: 'none'
                }}
              >
                <i className="fab fa-whatsapp" style={{ fontSize: '1.2rem' }}></i>
                Book This Package
              </button>
            </div>

            <div className="package-highlights">
              <h4>Package Highlights</h4>
              <ul>
                <li>✓ Best price guarantee</li>
                <li>✓ Free cancellation</li>
                <li>✓ Instant confirmation</li>
                <li>✓ 24/7 customer support</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Full image preview modal */}
    {isImageModalOpen && packageItem?.image && (
      <div 
        className="image-modal-overlay" 
        onClick={() => setIsImageModalOpen(false)}
        role="dialog"
        aria-modal="true"
        aria-label="Image preview"
      >
        <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
          <button 
            className="image-modal-close" 
            onClick={() => setIsImageModalOpen(false)}
            aria-label="Close preview"
          >
            <i className="fas fa-times"></i>
          </button>
          <div className="image-modal-image-container">
            <img 
              src={packageItem.image} 
              alt={packageItem.title}
              className="image-modal-image"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      </div>
    )}
  </div>
);
};

export default PackageDetail;






